/**
 * Alternatives table with expandable rows showing fund-level metrics.
 * Each row shows latest NAV, TVPI, DPI, IRR, and staleness indicator.
 * Expandable section shows quarterly trend, cash flows, and source documents.
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronRight,
  ChevronDown,
  ExternalLink,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";

interface AlternativesTableProps {
  orgId: string | null;
  familyId?: string | null;
}

interface AltVehicle {
  id: string;
  vehicle_name: string;
  manager_name: string;
  commitment: number | null;
  currency: string;
}

interface PeriodMetrics {
  id: string;
  alt_id: string;
  period_end: string;
  period_kind: string;
  computed_nav: number | null;
  called_cum: number | null;
  called_period: number | null;
  distributed_cum: number | null;
  distributed_period: number | null;
  tvpi: number | null;
  dpi: number | null;
  irr: number | null;
  moic: number | null;
  source_doc_id: string | null;
}

// Calculate staleness in days
function calculateStaleDays(latestDate: string | null): number {
  if (!latestDate) return 999;
  const latest = new Date(latestDate);
  const now = new Date();
  const diffMs = now.getTime() - latest.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

// Get staleness badge color and label
function getStalenessInfo(days: number): { color: string; label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (days <= 30) {
    return { color: "bg-green-500", label: "OK", variant: "secondary" };
  }
  if (days <= 60) {
    return { color: "bg-yellow-500", label: `${days}d`, variant: "outline" };
  }
  if (days <= 90) {
    return { color: "bg-orange-500", label: `${days}d`, variant: "outline" };
  }
  return { color: "bg-red-500", label: `${days}d!`, variant: "destructive" };
}

// Format currency
function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "-";
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Format multiplier (TVPI, DPI, MOIC)
function formatMultiplier(value: number | null | undefined): string {
  if (value == null) return "-";
  return `${value.toFixed(2)}x`;
}

// Format percentage (IRR)
function formatPercent(value: number | null | undefined): string {
  if (value == null) return "-";
  return `${value.toFixed(1)}%`;
}

// Format date as quarter
function formatQuarter(dateStr: string): string {
  const date = new Date(dateStr);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  const year = date.getFullYear().toString().slice(-2);
  return `Q${quarter}'${year}`;
}

export function AlternativesTable({ orgId, familyId }: AlternativesTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Fetch alt vehicles
  const { data: vehicles, isLoading: vehiclesLoading } = useQuery({
    queryKey: ["alt-vehicles", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];
      let query = supabase
        .from("alt_vehicles")
        .select("*")
        .eq("org_id", orgId)
        .order("vehicle_name");

      if (familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as AltVehicle[];
    },
    enabled: !!orgId,
  });

  // Fetch all period metrics for these vehicles
  const vehicleIds = vehicles?.map((v) => v.id) || [];
  const { data: allMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["alt-metrics", vehicleIds],
    queryFn: async () => {
      if (vehicleIds.length === 0) return [];
      const { data, error } = await supabase
        .from("alt_period_metrics")
        .select("*")
        .in("alt_id", vehicleIds)
        .order("period_end", { ascending: false });

      if (error) throw error;
      return (data || []) as PeriodMetrics[];
    },
    enabled: vehicleIds.length > 0,
  });

  // Group metrics by vehicle
  const metricsByVehicle = useMemo(() => {
    const map = new Map<string, PeriodMetrics[]>();
    allMetrics?.forEach((m) => {
      const existing = map.get(m.alt_id) || [];
      existing.push(m);
      map.set(m.alt_id, existing);
    });
    return map;
  }, [allMetrics]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalNav = 0;
    let totalCommitment = 0;
    let staleCount = 0;

    vehicles?.forEach((vehicle) => {
      const metrics = metricsByVehicle.get(vehicle.id) || [];
      const latest = metrics[0];
      totalNav += latest?.computed_nav || 0;
      totalCommitment += vehicle.commitment || 0;

      const staleDays = calculateStaleDays(latest?.period_end || null);
      if (staleDays > 90) staleCount++;
    });

    return { totalNav, totalCommitment, unfunded: totalCommitment - totalNav, staleCount };
  }, [vehicles, metricsByVehicle]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (vehiclesLoading || metricsLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No alternative investments found.</p>
          <p className="text-sm text-muted-foreground mt-2">
            Upload quarterly statements to see your fund holdings here.
          </p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/data/ingestion">Upload Documents</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]"></TableHead>
            <TableHead>Fund</TableHead>
            <TableHead className="text-right">NAV</TableHead>
            <TableHead className="text-right">TVPI</TableHead>
            <TableHead className="text-right">DPI</TableHead>
            <TableHead className="text-right">IRR</TableHead>
            <TableHead className="text-center">Data Age</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vehicles.map((vehicle) => {
            const metrics = metricsByVehicle.get(vehicle.id) || [];
            const latest = metrics[0];
            const isExpanded = expandedIds.has(vehicle.id);
            const staleDays = calculateStaleDays(latest?.period_end || null);
            const stalenessInfo = getStalenessInfo(staleDays);

            // Calculate quarterly change
            const prevNav = metrics[1]?.computed_nav;
            const currentNav = latest?.computed_nav || 0;
            const navChange = prevNav && prevNav > 0 ? ((currentNav - prevNav) / prevNav) * 100 : null;

            return (
              <>
                <TableRow
                  key={vehicle.id}
                  className="cursor-pointer hover:bg-accent/50"
                  onClick={() => toggleExpanded(vehicle.id)}
                >
                  <TableCell>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{vehicle.vehicle_name}</div>
                      <div className="text-xs text-muted-foreground">{vehicle.manager_name}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="font-mono font-semibold">{formatCurrency(latest?.computed_nav)}</div>
                    {navChange !== null && (
                      <div className="flex items-center justify-end gap-1 text-xs">
                        {navChange >= 0 ? (
                          <TrendingUp className="h-3 w-3 text-green-600" />
                        ) : (
                          <TrendingDown className="h-3 w-3 text-red-600" />
                        )}
                        <span className={navChange >= 0 ? "text-green-600" : "text-red-600"}>
                          {navChange >= 0 ? "+" : ""}{navChange.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatMultiplier(latest?.tvpi)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatMultiplier(latest?.dpi)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatPercent(latest?.irr)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={stalenessInfo.variant} className="min-w-[50px]">
                      <div className={`h-2 w-2 rounded-full ${stalenessInfo.color} mr-1.5`} />
                      {stalenessInfo.label}
                    </Badge>
                  </TableCell>
                </TableRow>

                {/* Expanded Row */}
                {isExpanded && (
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={7} className="p-0">
                      <div className="p-4 space-y-4">
                        {/* Quarterly Marks */}
                        <div>
                          <h4 className="text-sm font-semibold mb-2">Quarterly Marks</h4>
                          <div className="grid grid-cols-4 gap-3">
                            {metrics.slice(0, 4).map((m) => {
                              const prevMetric = metrics.find(
                                (pm) => pm.period_end < m.period_end
                              );
                              const change =
                                prevMetric?.computed_nav && m.computed_nav
                                  ? ((m.computed_nav - prevMetric.computed_nav) /
                                      prevMetric.computed_nav) *
                                    100
                                  : null;

                              return (
                                <Card key={m.id} className="bg-background">
                                  <CardContent className="p-3">
                                    <div className="text-xs text-muted-foreground mb-1">
                                      {formatQuarter(m.period_end)}
                                    </div>
                                    <div className="font-mono font-semibold">
                                      {formatCurrency(m.computed_nav)}
                                    </div>
                                    {change !== null && (
                                      <div className="flex items-center gap-1 text-xs">
                                        {change >= 0 ? (
                                          <TrendingUp className="h-3 w-3 text-green-600" />
                                        ) : change < 0 ? (
                                          <TrendingDown className="h-3 w-3 text-red-600" />
                                        ) : (
                                          <Minus className="h-3 w-3 text-muted-foreground" />
                                        )}
                                        <span
                                          className={
                                            change >= 0
                                              ? "text-green-600"
                                              : "text-red-600"
                                          }
                                        >
                                          {change >= 0 ? "+" : ""}
                                          {change.toFixed(1)}%
                                        </span>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </div>

                        {/* Fund Details Row */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Cash Flows Summary */}
                          <div>
                            <h4 className="text-sm font-semibold mb-2">Cash Flows</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">Commitment:</span>
                                <span className="ml-2 font-mono">
                                  {formatCurrency(vehicle.commitment)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Called:</span>
                                <span className="ml-2 font-mono">
                                  {formatCurrency(latest?.called_cum)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Distributed:</span>
                                <span className="ml-2 font-mono">
                                  {formatCurrency(latest?.distributed_cum)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Unfunded:</span>
                                <span className="ml-2 font-mono">
                                  {formatCurrency(
                                    (vehicle.commitment || 0) - (latest?.called_cum || 0)
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Source Documents */}
                          <div>
                            <h4 className="text-sm font-semibold mb-2">Source Documents</h4>
                            {latest?.source_doc_id ? (
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <Link
                                  to={`/data/review/${latest.source_doc_id}`}
                                  className="text-sm text-primary hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Latest Statement
                                </Link>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No linked documents
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2 border-t">
                          <Button variant="outline" size="sm" asChild>
                            <Link
                              to={`/portfolio/alternatives?fund=${encodeURIComponent(vehicle.vehicle_name)}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View Full History
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}

          {/* Totals Row */}
          <TableRow className="border-t-2 bg-muted/50 font-semibold">
            <TableCell></TableCell>
            <TableCell>Total ({vehicles.length} funds)</TableCell>
            <TableCell className="text-right font-mono">
              {formatCurrency(totals.totalNav)}
            </TableCell>
            <TableCell className="text-right">-</TableCell>
            <TableCell className="text-right">-</TableCell>
            <TableCell className="text-right">-</TableCell>
            <TableCell className="text-center">
              {totals.staleCount > 0 && (
                <Badge variant="destructive">{totals.staleCount} stale</Badge>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
